import React, { memo, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import classNames, { Argument as ClassName } from 'classnames'

import Text from '../../../ui/Text'
import { PhaseButtonGroup } from '../../../partner/pages/InstallationWizard/Wizard.styles'
import { Combobox } from '../combobox'
import GroupedButton from '../GroupedButton'
import { itNetPhaseOptionsLb, standardPhaseOptions } from '../../../lib/phaseRotationsList'
import { EarthingSystem, LoadBalancerGroup } from '../../redux/api/web-api'
import { SLabelText } from '../../styles/Form'
import { InfoIcon } from '../../../lib/icons/info-icon'
import { EarthingSchemeLB } from '../../enums/earthingSchemes'
import { Phases } from '../../enums/phaseTypes'
import useText from '../../hooks/useText'

export type LoadbalancerPhaseSettingsWidgetProps = {
    earthingScheme?: EarthingSystem | null
    setEarthingScheme: (earthingScheme: EarthingSystem) => void
    loadbalancerGroup?: LoadBalancerGroup
    hasChargers?: boolean
    showInfo?: boolean
    customStyling?: boolean
    setEnableSave?: (enableSave: boolean) => void
    setPhasesValid: (isValid: boolean) => void
    setAllowedPhases: (phase?: string | null) => void
    isEarthingSystemLocked?: boolean
    classNameSubHeading?: ClassName
    classNameComponent?: ClassName
}

export const LoadbalancerPhaseSettingsWidget = memo(function LoadbalancerPhaseSettingsWidget({
    earthingScheme,
    setEarthingScheme,
    hasChargers,
    loadbalancerGroup,
    showInfo,
    setAllowedPhases,
    setEnableSave,
    classNameSubHeading,
    classNameComponent,
    isEarthingSystemLocked,
    setPhasesValid,
}: LoadbalancerPhaseSettingsWidgetProps): React.ReactElement {
    const text = useText()
    const itNetPhase = Phases.L1L2L3
    const [tnPhase, setTnPhase] = useState<string | null | undefined>(Phases.L1L2L3)
    const {
        control: phaseControl,
        watch,
        setValue,
        trigger,
        formState: { errors: phaseErrors, isValid: isPhaseValid },
    } = useForm<Record<string, string | null | undefined>>({
        mode: 'onChange',
    })

    const { phase1, phase2, phase3 } = watch()
    const earthingSchemeBasedPhases = earthingScheme === EarthingSchemeLB.IT ? itNetPhase : tnPhase
    const getPhase1 = () =>
        loadbalancerGroup?.allowedPhases ? loadbalancerGroup?.allowedPhasesParsed?.at(0) : Phases.L1
    const getPhase2 = () =>
        loadbalancerGroup?.allowedPhases ? loadbalancerGroup?.allowedPhasesParsed?.at(1) : Phases.L2
    const getPhase3 = () =>
        loadbalancerGroup?.allowedPhases ? loadbalancerGroup?.allowedPhasesParsed?.at(2) : Phases.L3

    useEffect(() => {
        if (earthingScheme === EarthingSchemeLB.TN) {
            setValue('phase1', getPhase1())
            setValue('phase2', getPhase2())
            setValue('phase3', getPhase3())
        } else {
            setValue('itnetPhase', itNetPhase)
        }
        trigger()
    }, [earthingScheme])

    useEffect(() => {
        setTnPhase(phase1 + (phase2 ? ',' + phase2 : '') + (phase3 ? ',' + phase3 : ''))
    }, [phase1, phase2, phase3])

    useEffect(() => {
        setPhasesValid(isPhaseValid)
        setAllowedPhases(earthingSchemeBasedPhases)
    }, [earthingSchemeBasedPhases, isPhaseValid, setAllowedPhases, setPhasesValid])

    const baseClassNamesSubHeading = 'flex flex-column'
    const baseClassNamesComponent = 'flex gap-4 flex-wrap'

    const dynamicClassNamesSubHeading = classNames(classNameSubHeading, baseClassNamesSubHeading)
    const dynamicClassNamesComponent = classNames(classNameComponent, baseClassNamesComponent)

    const isDisabledEarthingSystem = () => {
        if (isEarthingSystemLocked) return 'msgEarthingSystemisLocked'
        if (loadbalancerGroup?.active) return 'msgLbActive'
        if (hasChargers) return 'msgDisabledEarthingSystemHasChargers'
    }

    return (
        <div className={dynamicClassNamesComponent}>
            <div className={dynamicClassNamesSubHeading}>
                <div className="flex justify-content-between">
                    <SLabelText>
                        <Text code="lblEarthingSystem" />
                    </SLabelText>
                    {showInfo && (
                        <span className="mt-3">
                            <InfoIcon
                                data-testid="earthing-info-icon"
                                title={text('msgEarthingSystemInfoLB')}
                                size={22}
                            />
                        </span>
                    )}
                </div>
                <GroupedButton
                    value={earthingScheme}
                    id="earthing-systems"
                    disabled={isEarthingSystemLocked || loadbalancerGroup?.active || hasChargers}
                    disabledReason={isDisabledEarthingSystem()}
                    options={[
                        {
                            label: text('lblTN'),
                            value: 'TN',
                        },

                        {
                            label: text('lblITNet'),
                            value: 'IT',
                        },
                    ]}
                    onChange={(earthingScheme: EarthingSystem) => {
                        setEarthingScheme(earthingScheme)
                        setEnableSave?.(true)
                    }}
                />
            </div>

            {earthingScheme && (
                <div className={dynamicClassNamesSubHeading}>
                    <div className="flex justify-content-between">
                        <SLabelText required={true}>
                            <Text code="lblPhaseSettings" />
                        </SLabelText>
                        {showInfo && (
                            <span className="mt-3">
                                <InfoIcon
                                    data-testid="phase-info-icon"
                                    title={text('lblPhaseSettingsInfoLB')}
                                    size={22}
                                />
                            </span>
                        )}
                    </div>
                    <div className="flex flex-column gap-1">
                        {earthingScheme === EarthingSchemeLB.IT ? (
                            <Controller
                                name="itnetPhase"
                                control={phaseControl}
                                render={({ field: { value, ...rest } }) => (
                                    <Combobox
                                        data-testid="itNet-combobox"
                                        id={'itnetPhase'}
                                        enableDefaultOption={false}
                                        options={itNetPhaseOptionsLb}
                                        {...rest}
                                        disabled={true}
                                        disabledReason={'msgDisabledITnetPhaseModes'}
                                        value={value}
                                    />
                                )}
                            />
                        ) : (
                            <PhaseButtonGroup>
                                <Controller
                                    name="phase1"
                                    control={phaseControl}
                                    rules={{
                                        required: text('msgRequiredShort'),
                                        validate: (val) => earthingScheme === 'TN' && val !== null,
                                    }}
                                    render={({ field: { value, onChange, ...rest } }) => (
                                        <Combobox
                                            disabled={loadbalancerGroup?.active}
                                            data-testid="phase-1-combobox"
                                            labelText={text('lblPhaseNo', { phaseNo: '1' })}
                                            enableDefaultOption={false}
                                            id={'phase1'}
                                            options={standardPhaseOptions}
                                            {...rest}
                                            value={value}
                                            onChange={(option) => {
                                                onChange(option.value)
                                                setValue('phase2', null)
                                                setValue('phase3', null)
                                                setEnableSave?.(true)
                                            }}
                                            error={phaseErrors.phase1?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    name="phase2"
                                    control={phaseControl}
                                    render={({ field: { value, onChange, ...rest } }) => (
                                        <Combobox
                                            data-testid="phase-2-combobox"
                                            labelText={text('lblPhaseNo', { phaseNo: '2' })}
                                            id={'phase2'}
                                            enableDefaultOption={true}
                                            options={standardPhaseOptions.filter((phase) => phase.value !== phase1)}
                                            {...rest}
                                            value={value}
                                            onChange={(option) => {
                                                onChange(option.value)
                                                setValue('phase3', null)
                                                setEnableSave?.(true)
                                            }}
                                            disabled={!phase1 || loadbalancerGroup?.active}
                                            error={phaseErrors.phase2?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    name="phase3"
                                    control={phaseControl}
                                    render={({ field: { value, onChange, ...rest } }) => (
                                        <Combobox
                                            data-testid="phase-3-combobox"
                                            labelText={text('lblPhaseNo', { phaseNo: '3' })}
                                            id={'phase3'}
                                            enableDefaultOption={true}
                                            options={standardPhaseOptions.filter(
                                                (phase) => phase.value !== phase1 && phase.value !== phase2
                                            )}
                                            {...rest}
                                            value={value}
                                            onChange={(option) => {
                                                onChange(option.value)
                                                setEnableSave?.(true)
                                            }}
                                            disabled={!phase2 || loadbalancerGroup?.active}
                                            error={phaseErrors.phase3?.message}
                                        />
                                    )}
                                />
                            </PhaseButtonGroup>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
})
